// ============================================================
// DESIGN SYSTEM — VetApp Hospital Veterinario
// src/theme/index.js
// ============================================================
// Sistema de diseño unificado de toda la aplicación.
// Se importa en la raíz: import theme from './theme';
//                        <ThemeProvider theme={theme}>
// ============================================================

import { createTheme, alpha } from '@mui/material/styles';

// ─────────────────────────────────────────────────────────────
// 1. TOKENS DE COLOR
// ─────────────────────────────────────────────────────────────

export const COLORS = {
  // ── Identidad de marca ──
  primary:    '#1A3A5C',
  teal:       '#2CB8A0',
  background: '#F4F6F8',

  // ── Semánticos clínicos ──
  danger:     '#DC2626',
  warning:    '#F59E0B',
  success:    '#10B981',
  info:       '#3B82F6',

  // ── Especies ──
  canino:     '#FF9800',
  felino:     '#AB47BC',
  exotico:    '#42A5F5',

  // ── Módulos ──
  lab:        '#1D4ED8',
  pharmacy:   '#15803D',
  surgery:    '#DC2626',
  caja:       '#92400E',

  // ── Neutrales ──
  border:     '#E2E8F0',
  muted:      '#64748B',
};

// Paleta de fondos suaves para chips y badges
export const COLORS_LIGHT = {
  danger:  '#FEE2E2',
  warning: '#FEF3C7',
  success: '#D1FAE5',
  info:    '#DBEAFE',
  canino:  '#FFF3E0',
  felino:  '#F3E5F5',
  lab:     '#EFF6FF',
  pharmacy:'#ECFDF5',
};

// Paleta de texto para usar sobre fondos claros
export const COLORS_DARK = {
  danger:  '#991B1B',
  warning: '#92400E',
  success: '#065F46',
  info:    '#1E40AF',
  canino:  '#C2410C',
  felino:  '#7B21A8',
  lab:     '#1D4ED8',
  pharmacy:'#065F46',
};

// ─────────────────────────────────────────────────────────────
// 2. TEMA MUI
// ─────────────────────────────────────────────────────────────

const theme = createTheme({
  palette: {
    primary: {
      main:         COLORS.primary,
      light:        '#2A5A8C',
      dark:         '#0F2340',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main:         COLORS.teal,
      light:        '#4DD4BD',
      dark:         '#1A9080',
      contrastText: '#FFFFFF',
    },
    error: {
      main:         COLORS.danger,
      light:        '#FEE2E2',
      dark:         '#991B1B',
    },
    warning: {
      main:         COLORS.warning,
      light:        '#FEF3C7',
      dark:         '#92400E',
    },
    success: {
      main:         COLORS.success,
      light:        '#D1FAE5',
      dark:         '#065F46',
    },
    info: {
      main:         COLORS.info,
      light:        '#DBEAFE',
      dark:         '#1E40AF',
    },
    background: {
      default: COLORS.background,
      paper:   '#FFFFFF',
    },
    text: {
      primary:   '#111827',
      secondary: '#64748B',
      disabled:  '#9CA3AF',
    },
    divider: COLORS.border,
  },

  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),

    h1: { fontSize: '2rem',    fontWeight: 700, color: COLORS.primary, lineHeight: 1.2 },
    h2: { fontSize: '1.5rem',  fontWeight: 700, color: COLORS.primary, lineHeight: 1.3 },
    h3: { fontSize: '1.25rem', fontWeight: 700, color: COLORS.primary, lineHeight: 1.4 },
    h4: { fontSize: '1.125rem',fontWeight: 700, color: COLORS.primary, lineHeight: 1.4 },
    h5: { fontSize: '1rem',    fontWeight: 700, color: COLORS.primary, lineHeight: 1.5 },
    h6: { fontSize: '0.875rem',fontWeight: 700, color: COLORS.primary, lineHeight: 1.5 },
    body1: { fontSize: '0.875rem', lineHeight: 1.6 },
    body2: { fontSize: '0.8rem',   lineHeight: 1.6, color: '#374151' },
    caption: {
      fontSize:   '0.72rem',
      lineHeight: 1.5,
      color:      '#64748B',
    },
    overline: {
      fontSize:      '0.68rem',
      fontWeight:    500,
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
      color:         '#64748B',
    },
  },

  shape: {
    borderRadius: 8,
  },

  // Sombras apagadas: usamos bordes planos
  shadows: [
    'none',
    'none',
    'none',
    'none',
    '0 1px 4px rgba(0,0,0,0.08)',
    'none', 'none', 'none', 'none', 'none', 'none',
    'none', 'none', 'none', 'none', 'none', 'none',
    'none', 'none', 'none', 'none', 'none', 'none',
    'none', 'none',
  ],

  components: {
    // Fix global del bug de label superpuesto en selects
    MuiInputLabel: {
      defaultProps: { shrink: true },
    },
    MuiSelect: {
      defaultProps: { displayEmpty: true, notched: true },
    },
    MuiOutlinedInput: {
      defaultProps: { notched: true },
    },

    // Paper — siempre flat con borde
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border:       `1px solid ${COLORS.border}`,
          borderRadius: 12,
          backgroundImage: 'none',
        },
        outlined: {
          border: `1px solid ${COLORS.border}`,
        },
      },
    },

    // Card — sin sombra, con borde
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border:       `1px solid ${COLORS.border}`,
          borderRadius: 12,
          backgroundImage: 'none',
        },
      },
    },

    // Botón
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform:  'none',
          fontWeight:     700,
          borderRadius:   8,
          fontSize:       '0.82rem',
        },
        containedPrimary: {
          background:     COLORS.primary,
          '&:hover': { background: '#2A5A8C' },
        },
        containedSecondary: {
          background:     COLORS.teal,
          '&:hover': { background: '#1A9080' },
        },
        outlined: {
          borderColor:  COLORS.border,
          color:        COLORS.primary,
          '&:hover': { borderColor: COLORS.primary, background: alpha(COLORS.primary, 0.04) },
        },
      },
    },

    // Chip
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight:   600,
          borderRadius: 6,
          fontSize:     '0.72rem',
          height:       24,
        },
        sizeSmall: {
          fontSize: '0.65rem',
          height:   20,
        },
      },
    },

    // Table
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: COLORS.border,
          fontSize:    '0.8rem',
          padding:     '8px 12px',
        },
        head: {
          fontWeight:     700,
          fontSize:       '0.75rem',
          backgroundColor:'#F8FAFC',
          color:          COLORS.muted,
          letterSpacing:  '0.03em',
          textTransform:  'uppercase',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: '#F0F9FF' },
          '&:last-child td': { border: 0 },
        },
      },
    },

    // TextField — label siempre visible + tamaño compacto
    MuiTextField: {
      defaultProps: { size: 'small' },
    },

    // Tab
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight:    600,
          fontSize:      '0.8rem',
          minHeight:     44,
          '&.Mui-selected': { color: COLORS.primary },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: COLORS.teal, height: 3, borderRadius: '3px 3px 0 0' },
      },
    },

    // Dialog
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16, border: `1px solid ${COLORS.border}` },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.primary,
          color:           '#FFFFFF',
          fontWeight:      700,
          fontSize:        '1rem',
          padding:         '16px 24px',
        },
      },
    },

    // Alert
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8, fontSize: '0.8rem', padding: '6px 12px' },
        standardError:   { background: '#FEE2E2', color: '#991B1B', border: `1px solid #FECACA` },
        standardWarning: { background: '#FEF3C7', color: '#92400E', border: `1px solid #FDE68A` },
        standardSuccess: { background: '#D1FAE5', color: '#065F46', border: `1px solid #A7F3D0` },
        standardInfo:    { background: '#DBEAFE', color: '#1E40AF', border: `1px solid #BFDBFE` },
      },
    },

    // Accordion
    MuiAccordion: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border:           `1px solid ${COLORS.border}`,
          borderRadius:     '12px !important',
          '&:before':       { display: 'none' },
          '& + &':          { marginTop: 8 },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.primary,
          borderRadius:    '12px 12px 0 0',
          '&.Mui-expanded': { borderRadius: '12px 12px 0 0' },
          '& .MuiAccordionSummary-expandIconWrapper': { color: '#FFFFFF' },
        },
        content: { '& .MuiTypography-root': { color: '#FFFFFF', fontWeight: 700 } },
      },
    },

    // Skeleton
    MuiSkeleton: {
      styleOverrides: {
        root: { borderRadius: 6 },
        wave: { background: 'linear-gradient(90deg, #F1F5F9 25%, #E2E8F0 50%, #F1F5F9 75%)' },
      },
    },

    // Tooltip
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1E293B',
          fontSize:        '0.72rem',
          borderRadius:    6,
          maxWidth:        240,
        },
        arrow: { color: '#1E293B' },
      },
    },

    // Snackbar
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        autoHideDuration: 4000,
      },
    },

    // LinearProgress
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, height: 6 },
        bar:  { borderRadius: 4 },
      },
    },

    // Avatar
    MuiAvatar: {
      styleOverrides: {
        root: { fontWeight: 700, fontSize: '0.85rem' },
      },
    },
  },
});

export default theme;
